import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  AlertTriangle, 
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Info
} from 'lucide-react';
import { tenantService } from '../../../core/services/firebase/firestore';
import { useAuth } from '../../auth/AuthContext';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  verified: boolean;
}

interface DomainRegistry {
  tenantId: string;
  domains: string[];
  primaryDomain: string;
  verified: boolean;
  dnsRecords?: DnsRecord[];
}

export const DomainManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [registry, setRegistry] = useState<DomainRegistry | null>(null);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tenant ID from user or config
  // In a real app, this comes from the authenticated user's tenant
  const tenantId = user?.uid || 'pousada-modelo'; // Mock for dev

  useEffect(() => {
    loadRegistry();
  }, [tenantId]);

  const loadRegistry = async () => {
    setLoading(true);
    try {
      const data = await tenantService.getTenantRegistry(tenantId);
      if (data) {
        setRegistry(data as DomainRegistry);
      } else {
        // Create initial registry if not exists
        const initial = {
          tenantId,
          domains: [`${tenantId}.nossapesca.com`],
          primaryDomain: `${tenantId}.nossapesca.com`,
          verified: true
        };
        await tenantService.updateTenantRegistry(tenantId, initial);
        setRegistry(initial);
      }
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar configurações de domínio.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain || !registry) return;
    
    // Simple validation
    if (!newDomain.includes('.') || newDomain.length < 4) {
      setError('Domínio inválido.');
      return;
    }

    if (registry.domains.includes(newDomain)) {
      setError('Este domínio já está cadastrado.');
      return;
    }

    const updatedDomains = [...registry.domains, newDomain];
    try {
      await tenantService.updateTenantRegistry(tenantId, { domains: updatedDomains });
      setRegistry({ ...registry, domains: updatedDomains });
      setNewDomain('');
      setError(null);
    } catch (err) {
      setError('Erro ao adicionar domínio.');
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!registry || domain === registry.primaryDomain) return;

    if (!confirm(`Tem certeza que deseja remover o domínio ${domain}?`)) return;

    const updatedDomains = registry.domains.filter(d => d !== domain);
    try {
      await tenantService.updateTenantRegistry(tenantId, { domains: updatedDomains });
      setRegistry({ ...registry, domains: updatedDomains });
    } catch (err) {
      setError('Erro ao remover domínio.');
    }
  };

  const handleSetPrimary = async (domain: string) => {
    if (!registry) return;
    try {
      await tenantService.updateTenantRegistry(tenantId, { primaryDomain: domain });
      setRegistry({ ...registry, primaryDomain: domain });
    } catch (err) {
      setError('Erro ao definir domínio principal.');
    }
  };

  const handleVerify = async (domain: string) => {
    setIsVerifying(true);
    try {
      const result: any = await tenantService.checkDnsStatus(tenantId, domain);
      if (result.verified) {
        // Update registry verified status if it's the primary domain
        if (domain === registry?.primaryDomain) {
          await tenantService.updateTenantRegistry(tenantId, { verified: true });
          setRegistry(prev => prev ? { ...prev, verified: true } : null);
        }
        alert(`DNS Verificado com sucesso para ${domain}!`);
      } else {
        alert(`O domínio ${domain} ainda não está apontando para nossos servidores. Aguarde a propagação (até 24h).`);
      }
    } catch (err) {
      alert('Erro na verificação.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-black text-gray-900 flex items-center gap-2">
            <Globe className="text-blue-600" size={32} />
            Configurações de Domínio
          </h1>
          <p className="text-gray-600 mt-1">Gerencie os domínios e subdomínios que apontam para o seu site.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Domain List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex justify-between items-center">
              <h2 className="font-bold text-gray-800">Seus Domínios</h2>
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full font-bold">
                {registry?.domains.length} domínios
              </span>
            </div>

            <div className="divide-y divide-gray-50">
              {registry?.domains.map((domain) => (
                <div key={domain} className="p-6 hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${domain === registry.primaryDomain ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Globe size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{domain}</span>
                          {domain === registry.primaryDomain && (
                            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-black uppercase tracking-tighter">
                              Principal
                            </span>
                          )}
                          {(domain.includes('.pescar.app') || domain.includes('.nossapesca.com')) && (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-tighter">
                              Sistema
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            {registry.verified ? (
                              <><CheckCircle2 size={14} className="text-green-500" /> Verificado</>
                            ) : (
                              <><AlertTriangle size={14} className="text-orange-500" /> Pendente</>
                            )}
                          </span>
                          <a href={`https://${domain}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                            Acessar <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!registry.verified && (
                        <button 
                          onClick={() => handleVerify(domain)}
                          disabled={isVerifying}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Verificar DNS"
                        >
                          <RefreshCw size={18} className={isVerifying ? 'animate-spin' : ''} />
                        </button>
                      )}
                      {domain !== registry.primaryDomain && (
                        <>
                          <button 
                            onClick={() => handleSetPrimary(domain)}
                            className="text-xs font-bold text-gray-500 hover:text-blue-600 px-3 py-2 rounded-lg hover:bg-white"
                          >
                            Tornar Principal
                          </button>
                          <button 
                            onClick={() => handleRemoveDomain(domain)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remover domínio"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100">
              <form onSubmit={handleAddDomain} className="flex gap-2">
                <input 
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="ex: pousadasol.com.br"
                  className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
                />
                <button 
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-md active:scale-95"
                >
                  <Plus size={18} />
                  Adicionar
                </button>
              </form>
              {error && <p className="text-xs text-red-500 mt-2 ml-1">{error}</p>}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
            <h3 className="font-bold text-blue-900 flex items-center gap-2 mb-2">
              <Info size={18} />
              Como configurar seu domínio customizado
            </h3>
            <p className="text-sm text-blue-800 leading-relaxed mb-4">
              Para usar um domínio próprio, você precisa configurar os registros DNS no seu provedor (GoDaddy, Registro.br, etc).
            </p>
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest block mb-1">Registro Tipo A</span>
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg font-mono text-sm border border-gray-100">
                  <span>@</span>
                  <span className="font-bold text-blue-700">151.101.1.195</span>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest block mb-1">Registro CNAME</span>
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg font-mono text-sm border border-gray-100">
                  <span>www</span>
                  <span className="font-bold text-blue-700">nossapesca.com</span>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                <div className="flex justify-between items-end mb-1">
                  <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Registro TXT</span>
                  <span className="text-[9px] text-gray-400">Verificação de Propriedade</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg font-mono text-xs border border-gray-100 overflow-hidden">
                  <span className="shrink-0 mr-2">@</span>
                  <span className="font-bold text-blue-700 truncate select-all">
                    pescar-app-verification=npesca-portal-v2x4
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-bold text-gray-800 mb-6">Status Global</h3>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-2xl ${registry?.verified ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                  {registry?.verified ? <ShieldCheck size={24} /> : <AlertTriangle size={24} />}
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-bold uppercase">Publicação</p>
                  <p className="font-bold text-gray-900">
                    {registry?.verified ? 'Totalmente Online' : 'Pendente Verificação'}
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">HTTPS (SSL)</span>
                  <span className="text-green-600 font-bold">Ativo (Auto)</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Propagation</span>
                  <span className="text-blue-600 font-bold">98%</span>
                </div>
                <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                  <div className="bg-blue-600 h-full w-[98%]"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-brand-orange to-orange-400 rounded-2xl shadow-lg p-6 text-white">
            <Globe className="mb-4 opacity-50" size={32} />
            <h3 className="font-black text-xl mb-2">Domínios Premium</h3>
            <p className="text-sm opacity-90 leading-relaxed mb-4">
              Domínios .com e .com.br incluídos no plano Premium com renovação grátis.
            </p>
            <button className="w-full bg-white text-brand-orange py-2 rounded-xl font-bold text-sm hover:bg-orange-50 transition-colors">
              Fazer Upgrade
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
